import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { PlatformAnalyticsOverview } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * `GET /api/v1/admin/analytics` — DEV §9.11, named exactly. Every read on
 * this domain is audited with a stated purpose (`reason`, ≥10 characters
 * — the identical `crossTenantReasonSchema` `GET /admin/users/:id/
 * memberships` already validates against). The query stays disabled
 * until the administrator has actually typed one — the same posture
 * `useMerchantsQuery` already takes for `GET /admin/merchants`, and for
 * the same reason: this app cannot invent a purpose on their behalf.
 */
export function usePlatformAnalyticsQuery(
  reason: string,
): UseQueryResult<PlatformAnalyticsOverview, ApiError> {
  const { accessToken } = useSession();
  const reasonValid = reason.trim().length >= 10;

  return useQuery({
    queryKey: ['platform-analytics', reason],
    queryFn: () => {
      const params = new URLSearchParams({ reason });
      return apiRequest<PlatformAnalyticsOverview>(`/admin/analytics?${params.toString()}`, {
        accessToken,
      });
    },
    enabled: accessToken !== null && reasonValid,
  });
}
